import { Injectable, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GiftcardService } from './giftcard.service';
import {
  AutomaticServiceProviderItemsResponse,
  GetBundleDetailsResponse,
  ManualServiceProviderItemsResponse,
  ServiceProviderDetailsResponse,
} from '../domain/giftcard.response';
import { ProviderType, RequestStatus } from '../domain/giftcard.enums';
import { BundleServiceProviderItemResponse } from '@shared';

@Injectable()
export class GiftcardStateService {
  private readonly _service = inject(GiftcardService);
  private readonly _giftcardId = signal<string | null>(null);
  private readonly _isBundleMode = signal(false);

  readonly infoResource = rxResource<
    ServiceProviderDetailsResponse | GetBundleDetailsResponse | null,
    { id: string | null; isBundleMode: boolean }
  >({
    params: () => ({
      id: this._giftcardId(),
      isBundleMode: this._isBundleMode(),
    }),
    stream: ({ params }) => {
      if (params.id === null) return of(null);

      const request$: Observable<
        ServiceProviderDetailsResponse | GetBundleDetailsResponse
      > = params.isBundleMode
        ? this._service.getBundleDetails(params.id)
        : this._service.getServiceProviderDetails(params.id);

      return request$.pipe(catchError(() => of(null)));
    },
  });

  readonly singleInfo = computed<ServiceProviderDetailsResponse | null>(() =>
    !this._isBundleMode()
      ? ((this.infoResource.value() as
          | ServiceProviderDetailsResponse
          | undefined) ?? null)
      : null,
  );
  readonly bundleInfo = computed<GetBundleDetailsResponse | null>(() =>
    this._isBundleMode()
      ? ((this.infoResource.value() as GetBundleDetailsResponse | undefined) ??
        null)
      : null,
  );

  readonly showCommission = computed(
    () => this.bundleInfo()?.showCommission ?? true,
  );

  /** Alias kept for backwards-compat in templates that use `info`. */
  readonly info = this.singleInfo;
  readonly loading = computed(() => this.infoResource.isLoading());
  readonly isBundleMode = computed(() => this._isBundleMode());
  readonly bundleId = computed(() =>
    this._isBundleMode() ? this._giftcardId() : null,
  );

  readonly providerType = computed(() => this.info()?.type ?? null);
  readonly isAutomatic = computed(
    () => this.providerType() === ProviderType.Automatic,
  );
  readonly isManual = computed(
    () => this.providerType() === ProviderType.Manual,
  );

  readonly manualItems = computed<ManualServiceProviderItemsResponse[]>(() =>
    (this.singleInfo()?.manualServiceProviderItems ?? []).map((item) => ({
      ...item,
      amounts: item.amounts
        ? [...item.amounts].sort((a, b) => Number(a.amount) - Number(b.amount))
        : item.amounts,
    })),
  );
  readonly automaticItems = computed<AutomaticServiceProviderItemsResponse[]>(
    () => this.singleInfo()?.automaticServiceProviderItems ?? [],
  );
  readonly bundleItems = computed<BundleServiceProviderItemResponse[]>(
    () => this.bundleInfo()?.bundleServiceProviderItemResponses ?? [],
  );

  readonly productName = computed(() => {
    if (this._isBundleMode()) return this.bundleInfo()?.displayName ?? null;
    const info = this.singleInfo();
    if (!info) return null;
    return `${info.serviceName ?? ''} ${info.providerName ?? ''}`.trim();
  });

  readonly status = RequestStatus.Pending;

  load(id: string, isBundleMode = false): void {
    this._isBundleMode.set(isBundleMode);
    this._giftcardId.set(id);
  }
}
